import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { UserStatus } from '@cargovibe/shared-types';

type BadgeVariant = React.ComponentProps<typeof Badge>['variant'];

const statusVariantMap: Record<UserStatus, BadgeVariant> = {
  [UserStatus.VERY_ACTIVE]: 'success',
  [UserStatus.ACTIVE]: 'default',
  [UserStatus.REACTIVATION_NEEDED]: 'warning',
  [UserStatus.DEACTIVATED]: 'outline',
};

const statusLabelKeyMap: Record<UserStatus, string> = {
  [UserStatus.VERY_ACTIVE]: 'users.veryActiveStatus',
  [UserStatus.ACTIVE]: 'users.activeUserStatus',
  [UserStatus.REACTIVATION_NEEDED]: 'users.reactivationNeeded',
  [UserStatus.DEACTIVATED]: 'users.deactivated',
};

const statusDescriptionKeyMap: Record<UserStatus, string> = {
  [UserStatus.VERY_ACTIVE]: 'users.statusDescriptions.veryActive',
  [UserStatus.ACTIVE]: 'users.statusDescriptions.active',
  [UserStatus.REACTIVATION_NEEDED]: 'users.statusDescriptions.reactivationNeeded',
  [UserStatus.DEACTIVATED]: 'users.statusDescriptions.deactivated',
};

interface UserStatusBadgeProps {
  status: UserStatus;
}

export function UserStatusBadge({ status }: Readonly<UserStatusBadgeProps>): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-full"
        >
          <Badge
            variant={statusVariantMap[status]}
            className="min-w-28 justify-center whitespace-nowrap"
          >
            {t(statusLabelKeyMap[status])}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium text-popover-foreground">{t(statusLabelKeyMap[status])}</p>
        <p className="text-muted-foreground">{t(statusDescriptionKeyMap[status])}</p>
      </TooltipContent>
    </Tooltip>
  );
}
